import { useCallback, useRef } from 'react';
import useDebouncedCallback from '@atlassian/jira-platform-use-debounce/src/utils/use-debounce-callback/index.tsx';
import { useIFCConfigContext } from '../context/index.tsx';

export const useIFCEditField = () => {
	const { ifcConfig, optimisticUpdateField, replaceFieldSetInColumns, optimisticDeleteField } =
		useIFCConfigContext();

	const currentFieldSetId = useRef<string>();
	const isUpdateInProgress = useRef<boolean>();

	const onStartFieldUpdate = useCallback(
		(
			id: string,
			field: {
				name: string;
				type: string;
				options?: { id: string; value: string }[] | undefined;
			},
		) => {
			isUpdateInProgress.current = true;
			optimisticUpdateField?.(id, field);
		},
		[optimisticUpdateField],
	);

	const [onDebouncedFieldUpdated] = useDebouncedCallback(
		(
			fieldId: string,
			field?: {
				name: string;
				type: string;
				collapsedName?: string | undefined;
			},
		) => {
			if (field?.collapsedName && currentFieldSetId.current && !isUpdateInProgress.current) {
				// skip saving fieldset config when there is no change in name
				if (field?.collapsedName !== currentFieldSetId.current) {
					replaceFieldSetInColumns?.(currentFieldSetId.current, field.collapsedName, fieldId);
				}
				currentFieldSetId.current = field?.collapsedName;
			}
		},
		1000,
	);

	const onFieldUpdated = useCallback(
		(
			fieldId: string,
			field?: {
				name: string;
				type: string;
				collapsedName?: string | undefined;
			},
		) => {
			isUpdateInProgress.current = false;
			onDebouncedFieldUpdated(fieldId, field);
		},
		[onDebouncedFieldUpdated],
	);

	const onFieldDeleted = useCallback(() => {
		if (currentFieldSetId.current) optimisticDeleteField?.(currentFieldSetId.current);
	}, [optimisticDeleteField]);

	const onUpdate = (fieldId: string, fieldSetId: string) => {
		currentFieldSetId.current = fieldSetId;

		ifcConfig?.onUpdate({
			fieldId,
			onStartFieldUpdate: (field) => onStartFieldUpdate(fieldId, field),
			onFieldUpdated,
			onFieldDeleted,
		});
	};

	return onUpdate;
};
